import { If } from 'components';
import { selectCurrentTeam } from 'containers/app/appSlice';
import {
  useAddCustomerAddressMutation,
  useEditCustomerAddressMutation,
  useLazyGetRecentSearchedCustomersQuery,
  useUpdateCustomerMutation
} from 'containers/customers/api';
import { CreateEditJobNavEnum, CreateJobErrorTypes } from 'containers/jobs/constant';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect, useState } from 'react';
import CreateCustomer from 'containers/create-customer/CreateCustomer';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import {
  selectAutoFilledData,
  selectBillingAddress,
  selectJobCustomer,
  selectJobValidationErrors,
  selectServiceAddress,
  setAutoFilledData,
  updateBillingAddress,
  updateJobCustomer,
  updateServiceAddress,
  updateServiceArea
} from '../createJobSlice';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import AddressModal from 'containers/jobs/components/AddressModal';
import JobFor from './JobFor';
import CustomerDetailsModal from './CustomerDetailsModal';
import {
  AddCustomerAddressPayload,
  CustomerAddress,
  CustomerObject,
  EditCustomerAddressPayload,
  UpdateCustomerPayload
} from 'containers/customers/customers.model';
import { CreateJobContextType } from 'containers/jobs/types';
import { getBorderStyles, getJobValidationErrors } from 'containers/jobs/utils';
import { API_SOURCE } from 'constants/common';

interface JobCustomerProps {
  isEdit?: boolean;
  context: React.Context<CreateJobContextType>;
}

const initlaAddressModalState = {
  type: null,
  isOpen: false,
  isEdit: false,
  address: null
};

const initialFormChangeState = {
  customer: false,
  serviceAddress: false,
  billingAddress: false
};

const JobCustomer: FC<JobCustomerProps> = ({ context }) => {
  const { selectedJobTab, setSelectedJobTab } = useContext<CreateJobContextType>(context);
  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState({
    customer: null,
    isOpen: false,
    type: CreateCustomerModalTypes.EDIT_CUSTOMER
  });
  const [openCustomerDetailsModal, setOpenCustomerDetailsModal] = useState(false);
  const [openAddressModal, setOpenAddressModal] = useState<{
    type: ADDRESS_TYPE;
    isOpen: boolean;
    isEdit: boolean;
    address;
  }>(initlaAddressModalState);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [hasChanged, setHasChanged] = useState(initialFormChangeState);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const selectedCustomer = useAppSelector(selectJobCustomer);
  const selectedServiceAddress = useAppSelector(selectServiceAddress);
  const selectedBillingAddress = useAppSelector(selectBillingAddress);
  const selectedJobValidations = useAppSelector(selectJobValidationErrors);
  const autoFilledData = useAppSelector(selectAutoFilledData);

  const dispatch = useAppDispatch();

  const [
    updateCustomer,
    { isSuccess: updateCustomerSuccess, data: updatedCustomerData, isLoading: isUpdateCustomerInProgress }
  ] = useUpdateCustomerMutation();
  const [addCustomerAddressMutation, { isSuccess: isAddAddressSuccess, isLoading: isLoadingAddAddress }] =
    useAddCustomerAddressMutation();
  const [editCustomerAddressMutation, { isSuccess: isEditAddressSuccess, isLoading: isLoadingEditAddress }] =
    useEditCustomerAddressMutation();

  const [getRecentSearchedCustomersQuery, { data: getRecentSearchedCustomersResponse }] =
    useLazyGetRecentSearchedCustomersQuery();

  const { customers: recentCustomers = [] } = getRecentSearchedCustomersResponse ?? {};

  const hasError = Boolean(
    getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.CUSTOMER) ||
      getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.SERVICE_ADDRESS)
  );

  const initialiseCustomerDetails = () => {
    const customer = { ...selectedCustomer };
    customer.serviceAddress = selectedServiceAddress ? [selectedServiceAddress] : [];
    customer.billingAddress = selectedBillingAddress ?? null;
    setCustomerDetails(customer);
  };

  const getRecentSearchedCustomers = () => {
    getRecentSearchedCustomersQuery({ teamId: currentTeam.id });
  };

  useEffect(() => {
    if (currentTeam?.id) {
      getRecentSearchedCustomers();
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    initialiseCustomerDetails();
  }, [selectedCustomer, selectedServiceAddress, selectedBillingAddress]);

  useEffect(() => {
    if (updateCustomerSuccess) {
      dispatch(updateJobCustomer(updatedCustomerData));
    }
  }, [updateCustomerSuccess]);

  useEffect(() => {
    if (isEditAddressSuccess || isAddAddressSuccess) {
      getRecentSearchedCustomers();
    }
  }, [isEditAddressSuccess, isAddAddressSuccess, isLoadingAddAddress, isLoadingEditAddress]);

  const isTabSelected = selectedJobTab === CreateEditJobNavEnum.JOB_FOR;

  const handleOpenCustomerDetailsModal = () => {
    setHasChanged(initialFormChangeState);
    setOpenCustomerDetailsModal(true);
  };

  const onCustomerAdd = customerObject => {
    setCustomerDetails(prev => ({ ...prev, ...customerObject }));
    handleCloseCustomerModal();
    setHasChanged(prev => ({ ...prev, customer: true }));
  };

  const handleRemoveCustomerFromJob = () => {
    dispatch(updateJobCustomer(null));
    dispatch(updateServiceAddress(null));
    dispatch(updateBillingAddress(null));
    dispatch(updateServiceArea(null));
    if (autoFilledData) {
      dispatch(setAutoFilledData({ ...autoFilledData, customerLatest: null }));
    }
  };

  const handleCloseAddressModal = () => {
    setOpenAddressModal(initlaAddressModalState);
  };

  const handleAddressAddition = addressDetails => {
    const addressPayload: CustomerAddress = {
      ...addressDetails,
      teamId: currentTeam.id.toString(),
      customerId: selectedCustomer['ID'],
      isBillingSameAsService: false,
      addressId: 0,
      addressNote: addressDetails.addressNote || '',
      addressName: addressDetails.addressName || ''
    };
    if (openAddressModal.type === ADDRESS_TYPE.SERVICE) {
      setCustomerDetails(prev => ({ ...prev, serviceAddress: [{ ...addressPayload }] }));
      setHasChanged(prev => ({ ...prev, serviceAddress: true }));
    } else if (openAddressModal.type === ADDRESS_TYPE.BILLING) {
      setCustomerDetails(prev => ({ ...prev, billingAddress: addressPayload }));
      setHasChanged(prev => ({ ...prev, billingAddress: true }));
    }
    handleCloseAddressModal();
  };

  const handleOpenAddressModal = ({ type, isEdit, address }: { type: ADDRESS_TYPE; isEdit: boolean; address }) => {
    setOpenAddressModal({ type, isOpen: true, isEdit, address });
  };

  const handleCloseCustomerDetailsModal = () => {
    setOpenCustomerDetailsModal(false);
    initialiseCustomerDetails();
  };

  const updateCustomerDetails = () => {
    const customer: UpdateCustomerPayload = {
      teamId: currentTeam.id.toString(),
      customerId: customerDetails.ID,
      name: customerDetails.name,
      lastName: customerDetails.lastName,
      companyName: customerDetails.companyName,
      phoneNumber: customerDetails.phoneNumber,
      email: customerDetails.email,
      referenceNumber: customerDetails.referenceNumber,
      note: customerDetails.note,
      source: API_SOURCE.WEB
    };
    updateCustomer(customer);
  };

  const updateAddressDetails = async (addressPayload: EditCustomerAddressPayload) => {
    const address = await editCustomerAddressMutation(addressPayload).unwrap();
    return address;
  };

  const addAddressDetails = async (addressPayload: AddCustomerAddressPayload) => {
    const address = await addCustomerAddressMutation(addressPayload).unwrap();
    return address;
  };

  const handleUpdateCustomerDetails = async (isServiceAddressEdit, isBillingAddressEdit, isBillingSameAsService) => {
    if (autoFilledData?.customerLatest && !autoFilledData.customerLatest?.id) {
      const updatedCustomer = {
        ...selectedCustomer,
        ...(hasChanged.customer ? customerDetails : {}),
        isBillingSameAsService
      };

      dispatch(updateJobCustomer(updatedCustomer));

      if (hasChanged.serviceAddress && customerDetails.serviceAddress.length > 0) {
        dispatch(updateServiceAddress(customerDetails.serviceAddress[0]));
      }
      if (hasChanged.billingAddress) {
        dispatch(updateBillingAddress(customerDetails.billingAddress));
      }
    } else {
      let serviceAddressId = customerDetails.serviceAddress[0]?.id ?? 0;

      if (hasChanged.customer) {
        updateCustomerDetails();
      }

      if (hasChanged.serviceAddress && customerDetails.serviceAddress.length > 0) {
        const serviceAddress: AddCustomerAddressPayload = {
          teamId: currentTeam.id.toString(),
          customerId: customerDetails.ID,
          type: ADDRESS_TYPE.SERVICE,
          street: customerDetails.serviceAddress[0].street,
          city: customerDetails.serviceAddress[0].city,
          state: customerDetails.serviceAddress[0].state,
          apartment: customerDetails.serviceAddress[0].apartment,
          zipCode: customerDetails.serviceAddress[0].zipCode,
          country: customerDetails.serviceAddress[0].country,
          addressNote: customerDetails.serviceAddress[0].addressNote?.trim(),
          addressName: customerDetails.serviceAddress[0].addressName,
          latitude: customerDetails.serviceAddress[0].latitude,
          longitude: customerDetails.serviceAddress[0].longitude,
          isBillingSameAsService: false,
          addressId: 0,
          source: API_SOURCE.WEB
        };
        if (isServiceAddressEdit) {
          const address = await updateAddressDetails({
            id: customerDetails.serviceAddress[0].id.toString(),
            body: serviceAddress
          });
          dispatch(updateServiceAddress(address));
        } else {
          const address = await addAddressDetails(serviceAddress);
          serviceAddressId = address.id;
          dispatch(updateServiceAddress(address));
        }
      }
      if (hasChanged.billingAddress) {
        const billingAddress: AddCustomerAddressPayload = {
          teamId: currentTeam.id.toString(),
          customerId: customerDetails.ID,
          type: ADDRESS_TYPE.BILLING,
          street: customerDetails.billingAddress ? customerDetails.billingAddress.street : '',
          city: customerDetails.billingAddress ? customerDetails.billingAddress.city : '',
          state: customerDetails.billingAddress ? customerDetails.billingAddress.state : '',
          apartment: customerDetails.billingAddress ? customerDetails.billingAddress.apartment : '',
          zipCode: customerDetails.billingAddress ? customerDetails.billingAddress.zipCode : '',
          country: customerDetails.billingAddress ? customerDetails.billingAddress.country : '',
          addressNote: customerDetails.billingAddress ? customerDetails.billingAddress.addressNote?.trim() : '',
          addressName: customerDetails.billingAddress ? customerDetails.billingAddress.addressName : '',
          latitude: customerDetails.billingAddress ? customerDetails.billingAddress.latitude : 0,
          longitude: customerDetails.billingAddress ? customerDetails.billingAddress.longitude : 0,
          isBillingSameAsService: isBillingSameAsService,
          addressId: serviceAddressId,
          source: API_SOURCE.WEB
        };
        if (isBillingSameAsService || !isBillingAddressEdit) {
          const address = await addAddressDetails(billingAddress);
          dispatch(updateBillingAddress(address));
        } else {
          const address = await updateAddressDetails({
            id: customerDetails.billingAddress.id.toString(),
            body: billingAddress
          });
          dispatch(updateBillingAddress(address));
        }
      }
      dispatch(
        updateJobCustomer({ ...selectedCustomer, isBillingSameAsService: isBillingSameAsService } as CustomerObject)
      );
    }
    handleCloseCustomerDetailsModal();
  };

  const handleOpenCustomerModal = customer => {
    setOpenCreateCustomerModal({ isOpen: true, customer, type: CreateCustomerModalTypes.EDIT_CUSTOMER });
  };

  const handleCloseCustomerModal = () => {
    setOpenCreateCustomerModal({
      customer: null,
      isOpen: false,
      type: CreateCustomerModalTypes.EDIT_CUSTOMER
    });
  };

  const handleSelectingBillingAsServiceAddress = (isSameAsServiceAddress: boolean) => {
    if (isSameAsServiceAddress) {
      setCustomerDetails(prev => ({ ...prev, billingAddress: prev.serviceAddress[0] }));
    } else {
      setCustomerDetails(prev => ({ ...prev, billingAddress: selectedBillingAddress ?? null }));
    }
    setHasChanged(prev => ({ ...prev, billingAddress: true }));
  };

  const handleOnFocus = () => {
    setSelectedJobTab(CreateEditJobNavEnum.JOB_FOR);
  };

  const borderStyle = getBorderStyles({
    isSelected: isTabSelected,
    hasError
  });

  return (
    <div className="w-full" onFocus={handleOnFocus}>
      <div className="mb-2 flex text-xl font-bold text-heading">
        Customer<span className="text-base text-error">*</span>
      </div>

      <div className={`relative rounded-md border py-6 px-8 ${borderStyle}`}>
        <JobFor
          handleRemoveCustomerFromJob={handleRemoveCustomerFromJob}
          handleOpenCustomerDetailsModal={handleOpenCustomerDetailsModal}
          recentCustomers={recentCustomers}
        />
      </div>
      <If condition={Boolean(openCreateCustomerModal.isOpen)}>
        <CreateCustomer
          open={openCreateCustomerModal.isOpen}
          onClose={handleCloseCustomerModal}
          panelType={openCreateCustomerModal.type}
          customerData={openCreateCustomerModal.customer}
          customerDataFetchSuccess={true}
          customerDataFetchLoading={false}
          onAddCustomer={onCustomerAdd}
          defaultName={''}
        />
      </If>
      {openAddressModal.isOpen && (
        <AddressModal
          isEdit={openAddressModal.isEdit}
          type={openAddressModal.type}
          onClose={handleCloseAddressModal}
          initialAddress={openAddressModal.address}
          onAddAddress={handleAddressAddition}
          isLoading={isLoadingAddAddress || isLoadingEditAddress}
        />
      )}
      {openCustomerDetailsModal && (
        <CustomerDetailsModal
          onOpenAddressModal={handleOpenAddressModal}
          customer={customerDetails}
          onClose={handleCloseCustomerDetailsModal}
          onOpenCustomerModal={handleOpenCustomerModal}
          onUpdateCustomerDetails={handleUpdateCustomerDetails}
          hasValuesChanged={hasChanged.customer || hasChanged.serviceAddress || hasChanged.billingAddress}
          isUpdateInProgress={isLoadingAddAddress || isLoadingEditAddress || isUpdateCustomerInProgress}
          onToggleBillingAddressCheck={handleSelectingBillingAsServiceAddress}
        />
      )}
    </div>
  );
};

export default JobCustomer;
